import type jsPDF from 'jspdf';
import { DayIntervals, Statistics, ActivityType } from '../../../../../model/activity.model';
import { formattedDuration } from '../../../../../utils/dateTime';
import { getDuration } from '../DaysIntervals/helper';
import convertDecimalToHexColor from '../../../../../utils/convertDecimalToHexColor';
import { GroupBy } from '../../../../../enum/activity.enum';
import dayjs from 'dayjs';
import { containsCJK } from './fontLoader';

export interface PdfDateRange {
  from: string;
  to: string;
}

export const addFooter = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by aTimeLogger - https://atimelogger.pro', pageWidth / 2, pageHeight - 10, { align: 'center' });
  }
};

export const addHeader = (
  doc: jsPDF,
  title: string,
  dateRange: PdfDateRange
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, 20, { align: 'center' });

  // Date range
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${dateRange.from} - ${dateRange.to}`, pageWidth / 2, 28, { align: 'center' });

  // Line separator
  doc.setLineWidth(0.5);
  doc.setDrawColor(200, 200, 200);
  doc.line(15, 32, pageWidth - 15, 32);

  return 40; // Return Y position for next content
};

const getStatisticsTitle = (groupBy?: GroupBy): string => {
  switch (groupBy) {
    case GroupBy.WEEK:
      return 'Weekly Statistics';
    case GroupBy.MONTH:
      return 'Monthly Statistics';
    case GroupBy.DAY:
    default:
      return 'Daily Statistics';
  }
};

export const addCharts = (
  doc: jsPDF,
  barChartImage: string,
  pieChartImage: string,
  statistics: Statistics,
  grouped: boolean,
  groupBy: GroupBy | undefined,
  startY: number
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Section title based on groupBy
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(getStatisticsTitle(groupBy), 15, startY);

  // Bar chart (full width)
  const barChartHeight = 50;
  doc.addImage(barChartImage, 'JPEG', 15, startY + 5, pageWidth - 30, barChartHeight);

  let currentY = startY + barChartHeight + 15;

  // Section title - Total Statistics
  doc.setFontSize(14);
  doc.text('Total Statistics', 15, currentY);

  // Calculate half width for pie chart area
  const contentWidth = pageWidth - 30; // 15mm margins on each side
  const halfWidth = contentWidth / 2;

  // Pie chart (left half)
  const pieSize = halfWidth - 10;
  const pieX = 15;
  doc.addImage(pieChartImage, 'JPEG', pieX, currentY + 5, pieSize, pieSize);

  // Statistics list (right half) with colored circles
  const statsX = 15 + halfWidth + 5;
  const statsEndX = pageWidth - 15; // Right margin
  const statsItems = grouped ? statistics.total.groupedStatistics : statistics.total.statistics;

  let statsY = currentY + 10;
  const lineHeight = 5;
  const circleRadius = 1.5;

  // Total time at the top in bold
  const totalDuration = formattedDuration(statistics.total.info.total * 1000);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(82, 61, 92); // Purple color
  doc.text('Total:', statsX, statsY);
  doc.text(totalDuration, statsEndX, statsY, { align: 'right' });
  statsY += lineHeight + 2;

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(statsX, statsY - 1, statsEndX, statsY - 1);
  statsY += 2;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  const maxItems = Math.floor((pieSize - 15) / lineHeight); // Account for total row
  const itemsToShow = statsItems.slice(0, maxItems);

  itemsToShow.forEach((item) => {
    const activityType = statistics.types.find(t => t.id === item.types[0]);
    const name = activityType?.name || 'Unknown';
    const duration = formattedDuration(item.duration * 1000);
    const color = activityType ? convertDecimalToHexColor(activityType.color) : '#888888';

    // Draw colored circle
    doc.setFillColor(color);
    doc.circle(statsX + circleRadius, statsY - 1, circleRadius, 'F');

    // Truncate long names
    const displayName = name.length > 20 ? name.substring(0, 17) + '...' : name;

    // Use CJK font if name contains CJK characters
    const fontName = containsCJK(name) ? 'NotoSansSC' : 'helvetica';
    doc.setFont(fontName, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(displayName, statsX + circleRadius * 2 + 2, statsY);
    doc.text(duration, statsEndX, statsY, { align: 'right' });
    statsY += lineHeight;
  });

  if (statsItems.length > maxItems) {
    doc.setFont('helvetica', 'italic');
    doc.text(`... and ${statsItems.length - maxItems} more`, statsX + circleRadius * 2 + 2, statsY);
  }

  return currentY + pieSize + 15;
};

export const addTotalSummary = (
  doc: jsPDF,
  statistics: Statistics,
  startY: number
): number => {
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);

  const totalMs = statistics.total.info.total * 1000;
  doc.text(`Total Time: ${formattedDuration(totalMs)}`, 15, startY);

  return startY + 10;
};

export const addRecordsTable = (
  doc: jsPDF,
  daysIntervals: DayIntervals[],
  activityTypes: ActivityType[],
  startY: number,
  autoTable: typeof import('jspdf-autotable').default
): number => {
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Records', 15, startY);

  const tableData: string[][] = [];

  // Plain list - flatten all intervals
  daysIntervals.forEach(dayInterval => {
    dayInterval.intervals.forEach(interval => {
      const activityType = activityTypes?.find(t => t.id === interval.typeId);
      const durationStr = getDuration(interval);
      const startDateTime = dayjs(interval.start).format('YYYY-MM-DD HH:mm');
      const endDateTime = dayjs(interval.finish).format('YYYY-MM-DD HH:mm');

      tableData.push([
        activityType?.name || 'Unknown',
        startDateTime,
        endDateTime,
        durationStr,
        interval.comment || ''
      ]);
    });
  });

  // Check if we need CJK font for this data
  const needsCJK = tableData.some(row => row.some(cell => containsCJK(cell)));
  const fontName = needsCJK ? 'NotoSansSC' : 'helvetica';

  autoTable(doc, {
    startY: startY + 5,
    head: [['Activity', 'Start', 'Finish', 'Duration', 'Comment']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [82, 61, 92],
      textColor: 255,
      fontStyle: 'bold',
      font: fontName
    },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: [0, 0, 0],
      font: fontName
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 32 },
      2: { cellWidth: 32 },
      3: { cellWidth: 18 },
      4: { cellWidth: 'auto' }
    }
  });

  // Return Y position after table
  return (doc as any).lastAutoTable?.finalY + 10 || startY + 50;
};
